import type { Address } from '@morpho-org/blue-sdk'
import type { ApiInputToken, Rewards } from '@morpho-org/dapp-core'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'

import type { VaultAllocationForPoints } from '@/components/charts/hooks/useVaultPoints'

import { getVaultWithPoints } from '@/components/charts/helpers/getPoints'
import { getParsedValue } from '@/helpers/formatters'
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider'

export interface VaultV1ApyInput {
  address: Address
  asset: ApiInputToken
  state?: {
    fee?: number | null
    netApyExcludingRewards?: number | null
    netApy?: number | null
    allocation: VaultAllocationForPoints[] | null
  } | null
  rewards: Rewards[]
}

interface VaultV1ApyParams {
  vaultV1: VaultV1ApyInput | undefined | null
}

/**
 * Calculates APY metrics for a V1 vault including performance fees and rewards.
 *
 * Formula: netApy = (nativeApy - performanceFee) + rewardsAprSum
 */
export function getVaultV1Apy({
  vaultV1,
  showPoints
}: VaultV1ApyParams & {
  showPoints?: boolean
}) {
  const apy = getParsedValue(vaultV1?.state?.netApyExcludingRewards, safeParseNumber)

  const netApy = getParsedValue(vaultV1?.state?.netApy, safeParseNumber)

  const performanceFee = getParsedValue(vaultV1?.state?.fee, safeParseNumber)

  const rewards = vaultV1?.rewards ?? []

  const points =
    showPoints && vaultV1?.asset?.chain?.id
      ? getVaultWithPoints(
          vaultV1.asset.chain.id,
          vaultV1.address,
          vaultV1.state?.allocation ?? null
        )
      : []

  return {
    apy,
    netApy,
    performanceFee,
    points,
    rewards
  }
}

/**
 * Hook that calculates and memoizes V1 vault APY metrics.
 */
export function useVaultV1Apy({ vaultV1 }: VaultV1ApyParams) {
  const { showPoints } = useFeatureFlags()

  return getVaultV1Apy({
    vaultV1,
    showPoints
  })
}
